import { Component, OnInit, Input, EventEmitter, Output } from '@angular/core';
import { AttributeComponent } from '../../interfaces/attribute.component';
import { AttributeDefinition } from '../../models/attribute-definition';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AttributeControlService } from '../../attribute-control-service';
import { AttributeControlBase } from '../../attribute-control-base';

@Component({
  selector: 'app-decimal',
  templateUrl: './decimal.component.html',
  styleUrls: ['./decimal.component.scss']
})
export class DecimalComponent extends AttributeControlBase implements OnInit, AttributeComponent {
  @Input() formGroup: FormGroup;
  @Input() attributeDefinition: AttributeDefinition;

  @Output() onRemove = new EventEmitter<number>();

  public maxAllowedValue = 9_999_999_999.999;

  constructor(public _attributeControlService: AttributeControlService<number | null>) {
    super();
  }

  ngOnInit(): void {
    const initial =
      this.attributeDefinition.value == null
        ? null
        : Number(this.attributeDefinition.value);

    this._attributeControlService.addFormControl(
      this.attributeDefinition.formControlName,
      this.attributeDefinition.isRequired,
      Number.isFinite(initial as number) ? (initial as number) : null,
      this.formGroup
    );

    this.keyRegex = /^[0-9.]$/;
    this.valueRegex = /^(?:\d{0,10})(?:\.\d{0,3})?$/;

    this.applyValidators([
      Validators.min(0),
      Validators.max(this.maxAllowedValue),
      Validators.pattern(this.valueRegex)
    ]);
  }

  public get control(): FormControl {
    return this.formGroup.get(this.attributeDefinition.formControlName) as FormControl;
  }

  get label(): string {
    return (this.attributeDefinition?.name || '').replace(/\s*[:*]+$/, '').trim();
  }

  value(): number | null {
    const raw = this.control?.value;
    const n = raw === null || raw === '' ? null : Number(raw);
    if (n === null || !Number.isFinite(n)) {
      this.attributeDefinition.value = null;
      return null;
    }

    const clamped = Math.max(0, Math.min(n, this.maxAllowedValue));
    const rounded = Math.round(clamped * 1000) / 1000;

    if (rounded !== n) this.control.setValue(rounded);

    this.attributeDefinition.value = rounded;
    return rounded;
  }

  getErrorMessage(): string {
    if (!this.control) return '';
    if (this.control.hasError('required')) return `${this.label} is required.`;
    if (this.control.hasError('min')) return `${this.label} must be ≥ 0.`;
    if (this.control.hasError('max')) return `${this.label} must be ≤ ${this.maxAllowedValue}.`;
    if (this.control.hasError('pattern')) return `${this.label} must be a number (up to 10 digits and 3 decimals).`;
    return `Invalid ${this.label.toLowerCase()}.`;
  }

  ngOnDestroy(): void {
    this._attributeControlService.removeControl(this.attributeDefinition.formControlName, this.formGroup);
  }
}
